import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspace } from "@cometchat-ai/auth/client";

import { apiRequest } from "./client";

export interface VariableDto {
  id: string;
  workspace_id: string;
  key: string;
  display_name: string;
  description: string | null;
  value_type: "text" | "number" | "boolean" | "date";
  is_sensitive: boolean;
  status: "active" | "archived";
  /** Agents with a permission on this variable (catalog list only). */
  agent_count?: number;
  created_at: string;
  updated_at: string;
}

export interface VariableUsageDto {
  count: number;
  agents: { id: string; name: string; mode: "read" | "write" | "both" }[];
}

export type VariableMode = "read" | "write" | "both";
export type TemporaryMode = "only_permanent" | "use" | "ask";

export interface VariablePermissionDto {
  agent_id: string;
  variable_id: string;
  key: string;
  display_name: string;
  value_type: string;
  is_sensitive: boolean;
  mode: VariableMode;
  /** How not-yet-confirmed values are handled: only_permanent (wait) · use (use
   *  directly) · ask (ask to confirm first). */
  temporary_mode: TemporaryMode;
  attached_at: string;
  /** Provenance (ENG-36201): `mention` rows are owned by the publish reconciler. */
  attached_via: "manual" | "mention";
}

export interface CreateVariableInput {
  key: string;
  display_name: string;
  description?: string;
  value_type: "text" | "number" | "boolean" | "date";
  is_sensitive: boolean;
}

export const variableKeys = {
  all: (slug: string) => ["variables", slug] as const,
  /** Prefix shared by every catalog query, regardless of status — invalidate
   *  this to refresh both the active and archived lists at once. */
  catalogBase: (slug: string) => ["variables", slug, "catalog"] as const,
  catalog: (slug: string, status: "active" | "archived" = "active") =>
    ["variables", slug, "catalog", status] as const,
  permissions: (slug: string, agentId: string) =>
    ["variables", slug, "agent", agentId, "permissions"] as const,
};

function useWsid() {
  const { workspace } = useWorkspace();
  return workspace.id;
}

export function useVariableCatalog(
  slug: string,
  status: "active" | "archived" = "active",
  enabled = true,
) {
  const wsid = useWsid();
  return useQuery({
    queryKey: variableKeys.catalog(slug, status),
    queryFn: () =>
      apiRequest<VariableDto[]>(
        "GET",
        `/api/workspaces/${wsid}/variable-catalog${status === "archived" ? "?status=archived" : ""}`,
      ),
    enabled,
  });
}

export function useCreateVariable(slug: string) {
  const wsid = useWsid();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVariableInput) =>
      apiRequest<VariableDto>("POST", `/api/workspaces/${wsid}/variable-catalog`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: variableKeys.catalogBase(slug) });
    },
  });
}

export function useArchiveVariable(slug: string) {
  const wsid = useWsid();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ id: string }>("DELETE", `/api/workspaces/${wsid}/variable-catalog/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: variableKeys.catalogBase(slug) });
    },
  });
}

export function useVariableUsage(slug: string, id: string | undefined, enabled = true) {
  const wsid = useWsid();
  return useQuery({
    queryKey: [...variableKeys.all(slug), "usage", id ?? ""] as const,
    queryFn: () =>
      apiRequest<VariableUsageDto>("GET", `/api/workspaces/${wsid}/variable-catalog/${id}/usage`),
    enabled: enabled && Boolean(id),
  });
}

export function useUnarchiveVariable(slug: string) {
  const wsid = useWsid();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<VariableDto>("PATCH", `/api/workspaces/${wsid}/variable-catalog/${id}`, {
        status: "active",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: variableKeys.catalogBase(slug) });
    },
  });
}

export function useAgentVariablePermissions(slug: string, agentId: string | undefined) {
  const wsid = useWsid();
  return useQuery({
    queryKey: variableKeys.permissions(slug, agentId ?? ""),
    queryFn: () =>
      apiRequest<VariablePermissionDto[]>(
        "GET",
        `/api/workspaces/${wsid}/agents/${agentId}/variable-permissions`,
      ),
    enabled: Boolean(agentId),
  });
}

export function useAttachVariable(slug: string, agentId: string) {
  const wsid = useWsid();
  const qc = useQueryClient();
  return useMutation({
    // POST upserts — also used to edit an attached variable's mode or its
    // temporary_mode independently (omitted fields are left unchanged).
    mutationFn: (
      args: string | { variableId: string; mode?: VariableMode; temporaryMode?: TemporaryMode },
    ) => {
      const body =
        typeof args === "string"
          ? { variable_id: args }
          : {
              variable_id: args.variableId,
              ...(args.mode !== undefined ? { mode: args.mode } : {}),
              ...(args.temporaryMode !== undefined ? { temporary_mode: args.temporaryMode } : {}),
            };
      return apiRequest<VariablePermissionDto>(
        "POST",
        `/api/workspaces/${wsid}/agents/${agentId}/variable-permissions`,
        body,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: variableKeys.permissions(slug, agentId) });
    },
  });
}

export function useDetachVariable(slug: string, agentId: string) {
  const wsid = useWsid();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variableId: string) =>
      apiRequest<void>(
        "DELETE",
        `/api/workspaces/${wsid}/agents/${agentId}/variable-permissions/${variableId}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: variableKeys.permissions(slug, agentId) });
    },
  });
}
